/* eslint-disable max-len */
import Moveable from 'moveable';
import Selecto from 'selecto';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import DragHandle from '@/components/CustomAbles/DragHandle';
import DeleteHandle from '@/components/CustomAbles/DeleteHandle';
import DuplicateHandle from '@/components/CustomAbles/DuplicateHandle';
import CropHandle from '@/components/CustomAbles/CropHandle';
import TextAIHandle from '@/components/CustomAbles/TextAIHandle';
import DimensionViewer from '@/components/CustomAbles/DimensionViewer';
import MenuHandle from '@/components/CustomAbles/MenuHandle';
import { convertingDifferentMeasurementUnitIntoPx } from '@/components/Helper';
import { throttle } from 'lodash';
import {
  findObjectById,
  findParentGroup,
  clearAllSelection,
  recalculateGroupBounds,
  updateEllipse,
} from '@/components/Canvas/utils/groupHelpers';
import {
  getTableRowContentMinHeight,
  isCornerResize,
  tableEdgeIndex,
  tableColWidths,
  tableRowHeights,
  tableColMins,
  tableRowMins,
  resizeTableTrack,
  tableTrackMin,
  tableUniformMinRatio,
  scaleTableUniformly,
} from '@/components/DesignObjects/TableObject';

export default {

  methods: {
    bindClickEventToMoveableControls() {
      const movebaleDirections = document.getElementsByClassName('moveable-control moveable-direction');
      for (let i = 0; i < movebaleDirections.length; i += 1) {
        movebaleDirections[i].onmousedown = this.onMoveableDirectionClick;
      }
    },
    removeClickEventToMoveableControls() {
      const movebaleDirections = document.getElementsByClassName('moveable-control moveable-direction');
      for (let i = 0; i < movebaleDirections.length; i++) {
        movebaleDirections[i].removeEventListener('mousedown', this.onMoveableDirectionClick);
      }
    },
    updateElementSnapping() {
      if (!this.moveableObj) { return; }
      if (!this.isAlignmentGuideOn) {
        this.moveableObj.elementGuidelines = [];
        return;
      }

      const selectedMoveable = this.getSelectedMoveableObjects();
      const selectedTargets  = this.buildMoveableTargetsForObjects(selectedMoveable);
      selectedMoveable.forEach((object) => {
        const element = document.getElementById(object.Id);
        if (element) { selectedTargets.push(element); }
      });
      if (
          this.activeObject
          && findParentGroup(this.objectList, this.activeObject.Id)
          && this.activeObject.isSelected
      ) {
        const activeElement = document.getElementById(this.activeObject.Id);
        if (activeElement) { selectedTargets.push(activeElement); }
      }
      const selectedElements = new Set(selectedTargets);

      this.moveableObj.elementGuidelines = Array
          .from(document.querySelectorAll('#canvas-wrapper .target'))
          .filter(element => !selectedElements.has(element));
    },

    initMoveableManager() {
      this.moveableObj = new Moveable(this.container.parentNode, {
        draggable:            true,
        target:               null,
        dragTarget:           '.drag-button',
        dragTargetSelf:       true,
        container:            this.container.parentNode,
        dragArea:             true,
        resizable:            true,
        scalable:             true,
        rotatable:            true,
        origin:               false,
        rotationPosition:     'top',
        originDraggable:      true,
        originRelative:       true,
        passDragArea:         true,
        throttleDrag:         1,
        throttleResize:       0,
        throttleScale:        0,
        throttleRotate:       1,
        keepRatio:            false,
        edge:                 true,
        maxWidth:             'auto',
        maxHeight:            'auto',
        minHeight:            'auto',
        minWidth:             'auto',
        snappable:            true,
        snapContainer:        this.container,
        verticalGuidelines:   [],
        horizontalGuidelines: [],
        snapElement:          this.isAlignmentGuideOn,
        snapDirections:       {
          top: true, left: true, bottom: true, right: true, center: true, middle: true,
        },
        elementSnapDirections: {
          top: true, left: true, bottom: true, right: true, center: true, middle: true,
        },
        defaultGroupRotate:         0,
        snapThreshold:              0,
        snapGridWidth:              0,
        snapGridHeight:             0,
        isDisplaySnapDigit:         true,
        snapDistFormat:             v => `${v}px`,
        ables:                      [DuplicateHandle, DragHandle, MenuHandle, DeleteHandle, CropHandle, DimensionViewer, TextAIHandle],
        props:                      {
          dragHandle: true, deleteHandle: true, duplicateHandle: true,
          cropHandle: false, dimensionViewer: true, textAIHandle: false, menuHandle: false,
        },
        defaultGroupOrigin:         '50% 50%',
        triggerAblesSimultaneously: true,
      })
          .on('dragStart', this.onMoveableDragStart)
          .on('drag', this.onMoveableDrag)
          .on('dragEnd', this.onMoveableDragEnd)
          .on('dragGroupStart', this.onMoveableGroupDragStart)
          .on('dragGroup', this.onMoveableGroupDrag)
          .on('dragGroupEnd', this.onMoveableGroupDragEnd)
          .on('resizeStart', this.onMoveableResizeStart)
          .on('resize', this.onMoveableResize)
          .on('resizeEnd', this.onMoveableResizeEnd)
          .on('scaleStart', this.onMoveableScaleStart)
          .on('scale', this.onMoveableScale)
          .on('scaleEnd', this.onMoveableScaleEnd)
          .on('scaleGroupStart', this.onMoveableScaleGroupStart)
          .on('scaleGroup', this.onMoveableScaleGroup)
          .on('scaleGroupEnd', this.onMoveableScaleGroupEnd)
          .on('rotateStart', this.onMoveableRotateStart)
          .on('rotate', this.onMoveableRotate)
          .on('rotateEnd', this.onMoveableRotateEnd)
          .on('rotateGroupStart', this.onMoveableRotateGroupStart)
          .on('rotateGroup', this.onMoveableRotateGroup)
          .on('rotateGroupEnd', this.onMoveableRotateGroupEnd);
    },

    setSnapThreshold(val) {
      this.moveableObj.snapHorizontalThreshold = val;
      this.moveableObj.snapVerticalThreshold   = val;
    },

    findObj(id) {
      return findObjectById(this.objectList, id);
    },

    canTransformObject(object) {
      return !!(object && !object.locked && object.IsVisible !== false);
    },

    hasTransformableSelection() {
      if (this.getSelectedMoveableObjects().length > 0) {
        return true;
      }
      if (this.activeObject && findParentGroup(this.objectList, this.activeObject.Id)) {
        return this.canTransformObject(this.activeObject);
      }
      return false;
    },

    syncMoveableForLockState() {
      if (!this.moveableObj) { return; }
      if (this.isTextEditModeActive && this.isTextEditModeActive()) {
        this.hideMoveableDuringTextEdit();
        return;
      }
      if (!this.hasTransformableSelection()) {
        this.moveableObj.target = null;
        if (typeof this.moveableObj.updateTarget === 'function') {
          this.moveableObj.updateTarget();
        }
        if (typeof this.hideBoundingBox === 'function') {
          this.hideBoundingBox(true);
        }
        return;
      }
      if (typeof this.hideBoundingBox === 'function') {
        this.hideBoundingBox(false);
      }
    },

    isGroupSelected(selectedTopLevel = null) {
      const top = selectedTopLevel || this.getSelectedTopLevelObjects();
      if (top.length > 1) { return true; }
      return !!(top.length === 1 && top[0] && top[0].type === 'group');
    },

    getSelectedTopLevelObjects(list = this.objectList) {
      return list.reduce((selected, obj) => {
        if (obj.type === 'pagedetails') { return selected; }
        if (obj.isSelected) {
          return this.canTransformObject(obj) ? selected.concat(obj) : selected;
        }
        if (obj.type === 'group' && obj.children) {
          return selected.concat(this.getSelectedTopLevelObjects(obj.children));
        }
        return selected;
      }, []);
    },
    getSelectedGroupChildObjects() {
      const selectedChildren = [];
      (this.objectList || []).forEach((group) => {
        if (group.type !== 'group' || group.isSelected || !group.children) { return; }
        group.children.forEach((child) => {
          if (child && child.isSelected && this.canTransformObject(child)) {
            selectedChildren.push(child);
          }
        });
      });
      return selectedChildren;
    },

    getSelectedMoveableObjects() {
      const topLevel = this.getSelectedTopLevelObjects();
      const children = this.getSelectedGroupChildObjects();
      const seen = new Set();
      const unique = [];
      topLevel.concat(children).forEach((obj) => {
        if (!obj || !obj.Id || seen.has(obj.Id)) { return; }
        seen.add(obj.Id);
        unique.push(obj);
      });
      return unique;
    },
    buildMoveableTargetsForObjects(objects) {
      const targets = [];
      const seen = new Set();
      const addTarget = (obj) => {
        if (!obj) { return; }
        if (obj.type === 'group') {
          (obj.children || []).forEach(addTarget);
          return;
        }
        if (!this.canTransformObject(obj) || seen.has(obj.Id)) { return; }
        const el = document.querySelector(`#canvas-wrapper [id='${obj.Id}']`)
            || document.getElementById(obj.Id);
        if (!el) { return; }
        seen.add(obj.Id);
        targets.push(el);
      };
      (objects || []).forEach(addTarget);
      return targets;
    },

    applyMultiSelectMoveableTargets(selectedTopLevel) {
      const moveableTargets = this.buildMoveableTargetsForObjects(selectedTopLevel);

      if (!this.moveableObj) { return moveableTargets; }
      this.moveableObj.target           = moveableTargets.length === 1
          ? moveableTargets[0]
          : (moveableTargets.length ? moveableTargets : null);
      this.moveableObj.resizable        = false;
      this.moveableObj.scalable         = true;
      this.moveableObj.keepRatio        = true;
      this.moveableObj.renderDirections = ['nw', 'ne', 'se', 'sw'];
      if (this.selectO) {
        this.suppressSelectoSelectEnd = true;
        this.selectO.setSelectedTargets(moveableTargets);
        this.suppressSelectoSelectEnd = false;
      }
      this.moveableObj.updateTarget();
      if (typeof this.moveableObj.updateRect === 'function') {
        this.moveableObj.updateRect();
      }
      this.refreshDragTarget();
      return moveableTargets;
    },

    refreshMoveableTargets() {
      return this.$nextTick(() => {
        if (this.isTextEditModeActive && this.isTextEditModeActive()) {
          this.hideMoveableDuringTextEdit();
          return;
        }
        if (!this.hasTransformableSelection()) {
          this.syncMoveableForLockState();
          return;
        }
        this.syncMoveableForLockState();
        const selectedMoveable = this.getSelectedMoveableObjects();
        if (this.isGroupSelected() || selectedMoveable.length > 1) {
          this.applyMultiSelectMoveableTargets(selectedMoveable);
        } else if (this.activeObject && this.moveableObj && this.canTransformObject(this.activeObject)) {
          const el = document.querySelector(`#canvas-wrapper [id='${this.activeObject.Id}']`)
              || document.getElementById(this.activeObject.Id);
          if (el) {
            this.moveableObj.target = el;
            this.moveableObj.updateTarget();
            if (this.selectO) {
              this.selectO.setSelectedTargets([el]);
            }
          }
          this.refreshDragTarget();
        }
      });
    },

    applyLiveCornerClip(el, object, width, height) {
      if (!el || !object || object.type !== 'image') {
        return;
      }
      const clipEl = el.querySelector('.corner-clip');
      if (!clipEl) {
        return;
      }
      el.style.clipPath = 'none';
      const ratio = Math.min(Number(object.cornerRadius / 2) || 0, 1);
      if (ratio <= 0) {
        clipEl.style.clipPath = 'none';
        return;
      }
      const radius = ratio * 0.5 * Math.min(width, height);
      clipEl.style.clipPath = `inset(0 round ${radius}px)`;
    },

    restoreMoveableElementFromModel(el, object) {
      if (!el || !object) { return; }
      el.style.left = `${object.X || 0}px`;
      el.style.top  = `${object.Y || 0}px`;
      if (object.type !== 'text' && object.type !== 'table') {
        el.style.width  = `${object.Width || 0}px`;
        el.style.height = `${object.Height || 0}px`;
      }
      const isSvgImage = object.type === 'image'
          && object.ImageType
          && (object.ImageType === 'SVGImage'
              || object.ImageType.indexOf('FDICText') !== -1
              || object.ImageType.indexOf('FDICLogo') !== -1);
      el.style.transformOrigin = (object.type === 'text' || object.type === 'shape')
          ? 'center center'
          : 'top left';
      el.style.transform = (object.type === 'shape' || object.type === 'text' || object.type === 'table' || isSvgImage)
          ? `rotate(${object.RotateDegree || 0}deg) scale(${object.scaleX || 1},${object.scaleY || 1})`
          : `scale(${object.scaleX || 1},${object.scaleY || 1}) rotate(${object.RotateDegree || 0}deg)`;
    },

    refreshDragTarget() {
      if (!this.moveableObj) { return; }
      this.moveableObj.dragTarget = '.drag-button';
    },

    resolveTopLevelSelection(object, priorActive) {
      if (!object) { return null; }
      const parentGroup = findParentGroup(this.objectList, object.Id);

      if (object.type === 'table') {
        const tableEl = document.getElementById(object.Id);
        if (tableEl && tableEl.classList.contains('is-editing')) {
          return object;
        }
      }

      if (
          priorActive
          && priorActive.type === 'group'
          && priorActive.isSelected
          && parentGroup
          && parentGroup.Id === priorActive.Id
      ) {
        return parentGroup;
      }

      if (
          priorActive
          && priorActive.Id === object.Id
          && parentGroup
          && !parentGroup.isSelected
      ) {
        return object;
      }

      return parentGroup || object;
    },

    // Drag Event
    onMoveableDragStart(e) {
      this.moveableObj.snappable = this.isAlignmentGuideOn;
      const obj = this.findObj(e.target.id);
      if (!obj || !this.canTransformObject(obj)) { return; }
      if (obj.type === 'table') {
        EventBus.$emit('tableExitEditOnMove', obj.Id);
      }
      e.set([obj.X || 0, obj.Y || 0]);
    },

    onMoveableDrag(e) {
      const obj = this.findObj(e.target.id);
      if (!obj || !this.activeObject || !this.canTransformObject(obj)) { return; }
      if (obj.Id !== this.activeObject.Id) {
        const parent = findParentGroup(this.objectList, obj.Id);
        if (!parent || parent.Id !== this.activeObject.Id) { return; }
      }
      e.target.style.transform = e.transform;
    },

    onMoveableDragEnd(e) {
      const { target } = e;
      if (!e.lastEvent) { return; }
      const selectedObject = this.getObjectListObjectFromDomElement(target);
      if (!selectedObject) { return; }
      [selectedObject.X, selectedObject.Y] = e.lastEvent.beforeTranslate;
      this.restoreMoveableElementFromModel(target, selectedObject);
      const parent = findParentGroup(this.objectList, selectedObject.Id);
      if (parent) {
        recalculateGroupBounds(parent);
      }
      this.refreshMoveableTargets();
      this.emitUndoRedoRequest('called by drag end');
    },

    onMoveableGroupDragStart(e) {
      e.events.forEach((ev) => {
        const obj = this.findObj(ev.target.id);
        if (!obj) { return; }
        ev.set([obj.X || 0, obj.Y || 0]);
      });
    },

    onMoveableGroupDrag(e) {
      e.events.forEach((ev) => {
        ev.target.style.transform = ev.transform;
      });
    },

    onMoveableGroupDragEnd(e) {
      const touchedGroups = new Set();
      e.events.forEach((ev) => {
        if (!ev.isDrag || !ev.lastEvent) { return; }
        const selectedObject = this.findObj(ev.target.id);
        if (!selectedObject) { return; }
        [selectedObject.X, selectedObject.Y] = ev.lastEvent.beforeTranslate;
        this.restoreMoveableElementFromModel(ev.target, selectedObject);
        const parent = findParentGroup(this.objectList, selectedObject.Id);
        if (parent) { touchedGroups.add(parent); }
      });
      touchedGroups.forEach(group => recalculateGroupBounds(group));
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.refreshMoveableTargets();
      this.emitUndoRedoRequest('called by group drag end');
    },

    // Resizing Event
    onMoveableResizeStart(e) {
      this.moveableObj.snappable = this.isAlignmentGuideOn;
      this.setSnapThreshold(0);
      const obj = this.findObj(e.target.id);
      this.moveableObj.throttleResize = (obj && obj.type === 'table') ? 1 : 0;
      if (obj && obj.type === 'table') {
        EventBus.$emit('tableHideDots', obj.Id);
        const direction = (e.direction || [0, 0]).slice();
        const limits    = this.getTableEdgeResizeLimits(obj, direction);
        e.setMin([limits.minWidth, limits.minHeight]);
        EventBus.$emit('tableEdgeResizeStart', { tableId: obj.Id, direction, limits });
      }
      if (e.target.getAttribute('data-type') === 'text') {
        const minHeight = parseInt(window.getComputedStyle(e.target.firstElementChild).getPropertyValue('height'), 10);
        e.setMin([0, minHeight + 10]);
      }
      this.moveableObj.keepRatio = (Math.abs(e.direction[0]) === Math.abs(e.direction[1]));
      const { X, Y } = this.getObjectListObjectFromDomElement(e.target);
      e.dragStart && e.dragStart.set([X, Y]);
      e.setOrigin(['%', '%']);
    },

    onMoveableResize(e) {
      const selectedObject = this.findObj(e.target.id);
      if (!selectedObject) { return; }
      if (selectedObject.type === 'table') {
        const [x, y] = (e.drag && e.drag.beforeTranslate) || [selectedObject.X, selectedObject.Y];
        EventBus.$emit('tableEdgeResizePreview', {
          tableId: selectedObject.Id,
          width:   e.width,
          height:  e.height,
          x,
          y,
        });
        return;
      }
      e.target.style.width  = `${e.width}px`;
      e.target.style.height = `${e.height}px`;
      this.applyLiveCornerClip(e.target, selectedObject, e.width, e.height);
      const resizeSvg = e.target.querySelector('svg');
      const flipX = selectedObject.IsHFlipped === true || selectedObject.IsHFlipped === 'true';
      const flipY = selectedObject.IsVFlipped === true || selectedObject.IsVFlipped === 'true';
      if ((selectedObject.type === 'shape' || selectedObject.type === 'image') && resizeSvg && (selectedObject.type === 'shape' || selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)) {
        const width  = parseInt(e.width, 10);
        const height = parseInt(e.height, 10);
        if (width < 2 || height < 2) { return; }
        const isSvgImage = (selectedObject.type === 'image') && (selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1);
        const w = isSvgImage ? convertingDifferentMeasurementUnitIntoPx(resizeSvg.getAttribute('width')) : parseInt(resizeSvg.getAttribute('width'), 10);
        const h = isSvgImage ? convertingDifferentMeasurementUnitIntoPx(resizeSvg.getAttribute('height')) : parseInt(resizeSvg.getAttribute('height'), 10);
        let scaleX = width / w || 1;
        let scaleY = height / h || 1;
        if (flipX || flipY) {
          const flipHost = e.target.querySelector('.svg-height') || resizeSvg.parentElement;
          flipHost.style.width = `${width}px`;
          flipHost.style.height = `${height}px`;
          flipHost.style.transformOrigin = 'center center';
        }
        resizeSvg.style.transform = `scale(${scaleX}, ${scaleY})`;
      }
      e.target.style.transform = e.drag.transform;
    },

    onMoveableResizeEnd(e) {
      this.setSnapThreshold(5);
      const selectedObject = this.findObj(e.target.id);
      if (selectedObject && selectedObject.type === 'table') {
        EventBus.$emit('tableShowDots', selectedObject.Id);
        EventBus.$emit('tableEdgeResizeEnd', selectedObject.Id);
      }
      if (!selectedObject || !e.lastEvent) { return; }
      const resizeEndSvg = e.target.querySelector('svg');
      if ((selectedObject.type === 'shape' || selectedObject.type === 'image') && resizeEndSvg && (selectedObject.type === 'shape' || selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)) {
        const width  = parseInt(e.lastEvent.width, 10);
        const height = parseInt(e.lastEvent.height, 10);
        if (width < 2 || height < 2) { return; }
        const isSvgImage = (selectedObject.type === 'image') && (selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1);
        const w = isSvgImage ? convertingDifferentMeasurementUnitIntoPx(resizeEndSvg.getAttribute('width')) : parseInt(resizeEndSvg.getAttribute('width'), 10);
        const h = isSvgImage ? convertingDifferentMeasurementUnitIntoPx(resizeEndSvg.getAttribute('height')) : parseInt(resizeEndSvg.getAttribute('height'), 10);
        let scaleX = width / w || 1;
        let scaleY = height / h || 1;
        if (selectedObject.shapeType === 'rectangle') {
          scaleX = 1;
          scaleY = 1;
        }
        if (isSvgImage) {
          selectedObject.SvgProperty.svgScaleX = scaleX;
          selectedObject.SvgProperty.svgScaleY = scaleY;
        } else if (selectedObject.type === 'shape') {
          selectedObject.svgScaleX = scaleX;
          selectedObject.svgScaleY = scaleY;
        }
        resizeEndSvg.style.transformOrigin = 'left top';
        resizeEndSvg.style.transform = `scale(${scaleX},${scaleY})`;
        if (isSvgImage) {
          selectedObject.SvgProperty.content = resizeEndSvg.outerHTML;
          selectedObject.SvgProperty.style   = resizeEndSvg.style.transform;
        }
      }
      [selectedObject.X, selectedObject.Y] = e.lastEvent.drag.beforeTranslate;
      if (selectedObject.type === 'text' && selectedObject.Height > e.lastEvent.height) {
        const parentComputedHeight = window.getComputedStyle(e.target).getPropertyValue('height');
        const childComputedHeight  = window.getComputedStyle(e.target.firstElementChild).getPropertyValue('height');
        selectedObject.Height      = parseInt(parentComputedHeight, 10) > parseInt(childComputedHeight, 10) ? e.lastEvent.height : selectedObject.Height;
      } else {
        selectedObject.Width  = e.lastEvent.width;
        selectedObject.Height = e.lastEvent.height;
      }
      if (selectedObject.shapeType === 'ellipses') {
        updateEllipse(selectedObject);
        if (resizeEndSvg) {
          resizeEndSvg.style.transform = 'scale(1, 1)';
        }
      }
      if (selectedObject.type === 'table') {
        const direction = (e.lastEvent && e.lastEvent.direction) || e.direction || [0, 0];
        this.applyTableBoxResize(selectedObject, e.lastEvent.width, e.lastEvent.height, direction);
        this.anchorTableToFixedEdge(selectedObject, direction, e.lastEvent.width, e.lastEvent.height);
      }
      if (selectedObject.shapeType === 'text') {
        this.setParentHeightAfterTextResize(e.target);
      }
      this.restoreMoveableElementFromModel(e.target, selectedObject);
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.refreshMoveableTargets();
      this.$nextTick(() => {
        this.emitUndoRedoRequest('called by resize end');
      });
    },

    onMoveableScaleStart(e) {
      const selectedObject = this.findObj(e.target.id);
      if (!selectedObject) { return; }
      if (selectedObject.type === 'table') {
        EventBus.$emit('tableHideDots', selectedObject.Id);
      }
      this.moveableObj.keepRatio = (Math.abs(e.direction[0]) === Math.abs(e.direction[1]));
      this.moveableObj.snappable = this.isAlignmentGuideOn;
      this.setSnapThreshold(0);
      e.set([selectedObject.scaleX, selectedObject.scaleY]);
      e.setFixedDirection([0, 0]);
      if (e.target.getAttribute('data-type') !== 'text' && e.target.getAttribute('data-type') !== 'table') {
        const directionX = e.direction[0] === 1 ? -1 : 1;
        const directionY = e.direction[1] === 1 ? -1 : 1;
        e.setFixedDirection([directionX, directionY]);
      }
    },

    onMoveableScale(e) {
      e.target.style.transform = e.drag.transform;
    },

    onMoveableScaleEnd(e) {
      const selectedObject = this.findObj(e.target.id);
      if (selectedObject && selectedObject.type === 'table') {
        EventBus.$emit('tableShowDots', selectedObject.Id);
      }
      const ev  = { events: [e] };
      this.onMoveableScaleGroupEnd(ev);
      this.emitUndoRedoRequest('called by Scale end');
      this.setSnapThreshold(5);
    },

    onMoveableScaleGroupStart(e) {
      this.moveableObj.snappable = this.isAlignmentGuideOn;
      this.setSnapThreshold(0);
      e.events.forEach((ev) => {
        const selectedObject = this.findObj(ev.target.id);
        if (!selectedObject) { return; }
        ev.set([selectedObject.scaleX || 1, selectedObject.scaleY || 1]);
      });
    },

    onMoveableScaleGroup(e) {
      this.moveableObj.keepRatio = true;
      e.events.forEach((ev) => {
        ev.target.style.transform = ev.drag.transform;
      });
    },

    onMoveableScaleGroupEnd(e) {
      e.events.forEach((ev) => {
        if (!ev.lastEvent) { return; }
        const selectedObject = this.findObj(ev.target.id);
        if (!selectedObject) { return; }
        if (selectedObject.type === 'table') {
          EventBus.$emit('tableShowDots', selectedObject.Id);
        }

        let scaleX    = ev.lastEvent.scale[0];
        let scaleY    = ev.lastEvent.scale[1];
        let newWidth  = scaleX * selectedObject.Width;
        let newHeight = scaleY * selectedObject.Height;

        if ((selectedObject.type === 'shape' || selectedObject.type === 'image') && (ev.target.querySelector('svg')) && (selectedObject.type === 'shape' || selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)) {
          const targetChild = ev.target.querySelector('svg');
          newWidth  = Math.ceil(scaleX * parseInt(window.getComputedStyle(ev.target).getPropertyValue('width'), 10));
          newHeight = Math.ceil(scaleY * parseInt(window.getComputedStyle(ev.target).getPropertyValue('height'), 10));
          const childWidth  = parseInt(window.getComputedStyle(targetChild).getPropertyValue('width'), 10);
          const childHeight = parseInt(window.getComputedStyle(targetChild).getPropertyValue('height'), 10);
          scaleX = childWidth ? (newWidth / childWidth) : 1;
          scaleY = childHeight ? (newHeight / childHeight) : 1;
          if (selectedObject.shapeType === 'rectangle') {
            scaleX = 1;
            scaleY = 1;
          }
          if ((selectedObject.type === 'image') && (selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)) {
            selectedObject.SvgProperty.svgScaleX = scaleX;
            selectedObject.SvgProperty.svgScaleY = scaleY;
          } else if (selectedObject.type === 'shape') {
            selectedObject.svgScaleX = scaleX;
            selectedObject.svgScaleY = scaleY;
          }
          targetChild.style.transform = `scale(${scaleX},${scaleY})`;
          if ((selectedObject.type === 'image') && (selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)) {
            selectedObject.SvgProperty.content = targetChild.outerHTML;
            selectedObject.SvgProperty.style   = targetChild.style.transform;
          }
        }

        if (selectedObject.type === 'text') {
          selectedObject.Y      += ev.lastEvent.drag.beforeTranslate[1];
          selectedObject.X      += ev.lastEvent.drag.beforeTranslate[0];
          selectedObject.scaleX  = ev.lastEvent.scale[0];
          selectedObject.scaleY  = ev.lastEvent.scale[1];
        } else if (selectedObject.type === 'table') {
          selectedObject.Y      += ev.lastEvent.drag.beforeTranslate[1];
          selectedObject.X      += ev.lastEvent.drag.beforeTranslate[0];
          selectedObject.scaleX  = ev.lastEvent.scale[0];
          selectedObject.scaleY  = ev.lastEvent.scale[1];
        } else {
          let xOffset = 0;
          let yOffset = 0;
          if (selectedObject.type === 'shape') {
            xOffset = (selectedObject.Width - newWidth) / 2;
            yOffset = (selectedObject.Height - newHeight) / 2;
          }
          selectedObject.Y     += ev.lastEvent.drag.beforeTranslate[1] + Math.ceil(yOffset);
          selectedObject.X     += ev.lastEvent.drag.beforeTranslate[0] + Math.ceil(xOffset);
          selectedObject.Width  = newWidth;
          selectedObject.Height = newHeight;
          if (selectedObject.shapeType === 'ellipses') {
            updateEllipse(selectedObject);
            const ellipseSvg = ev.target.querySelector('svg');
            if (ellipseSvg) {
              ellipseSvg.style.transform = 'scale(1, 1)';
            }
          }
        }
        this.restoreMoveableElementFromModel(ev.target, selectedObject);
      });
      this.setSnapThreshold(5);
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.refreshMoveableTargets();
    },

    getTableEdgeResizeLimits(table, direction) {
      const widths  = tableColWidths(table);
      const heights = tableRowHeights(table);
      const corner  = isCornerResize(direction);

      if (corner) {
        const minRatio = tableUniformMinRatio(table);
        const width    = widths.reduce((s, v) => s + v, 0);
        const height   = heights.reduce((s, v) => s + v, 0);

        return {
          corner,
          minRatio,
          width,
          height,
          colIndex:  -1,
          rowIndex:  -1,
          minWidth:  width * minRatio,
          minHeight: height * minRatio,
        };
      }

      const colMins  = tableColMins(table);
      const rowMins  = tableRowMins(table);
      const colIndex = tableEdgeIndex(direction, 0, widths.length);
      const rowIndex = tableEdgeIndex(direction, 1, heights.length);

      return {
        corner,
        colIndex,
        rowIndex,
        colMins,
        rowMins,
        minWidth:  tableTrackMin(widths, colMins, colIndex, corner),
        minHeight: tableTrackMin(heights, rowMins, rowIndex, corner),
      };
    },

    tableCornerScaleRatio(limits, newWidth, newHeight) {
      const wr    = limits.width ? (newWidth / limits.width) : 1;
      const hr    = limits.height ? (newHeight / limits.height) : 1;
      const ratio = Math.sqrt(Math.max(wr, 0) * Math.max(hr, 0)) || 1;
      return Math.max(limits.minRatio || 0, ratio);
    },

    anchorTableToFixedEdge(table, direction, requestedWidth, requestedHeight) {
      const dx = direction[0] === -1 ? requestedWidth - table.Width : 0;
      const dy = direction[1] === -1 ? requestedHeight - table.Height : 0;
      if (!dx && !dy) { return; }
      const rad = ((table.RotateDegree || 0) * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const lx  = dx * (table.scaleX || 1);
      const ly  = dy * (table.scaleY || 1);

      table.X += (lx * cos) - (ly * sin);
      table.Y += (lx * sin) + (ly * cos);
    },

    applyTableBoxResize(table, newWidth, newHeight, direction) {
      const cols = table.cols || [];
      const rows = table.rows || [];
      if (!cols.length || !rows.length) return;

      const limits = this.getTableEdgeResizeLimits(table, direction);

      if (limits.corner) {
        const ratio = this.tableCornerScaleRatio(limits, newWidth, newHeight);
        scaleTableUniformly(table, ratio, (o, k, v) => this.$set(o, k, v));
        return;
      }

      const widths  = resizeTableTrack(tableColWidths(table), limits.colMins, newWidth, limits.colIndex, limits.corner);
      const heights = resizeTableTrack(tableRowHeights(table), limits.rowMins, newHeight, limits.rowIndex, limits.corner);

      cols.forEach((col, c) => { col.width = widths[c]; });
      rows.forEach((row, r) => {
        row.height = heights[r];
        row.manualHeight = heights[r] > limits.rowMins[r];
      });
      // Wider or narrower columns rewrap the text, so rows may need more height.
      if (direction[0] !== 0) {
        rows.forEach((row, r) => {
          row.height = Math.max(row.height, getTableRowContentMinHeight(table, r));
        });
      }
      table.Width  = cols.reduce((s, c) => s + (c.width || 0), 0);
      table.Height = rows.reduce((s, r) => s + (r.height || 0), 0);
    },

    setParentHeightAfterTextResize(element) {
      const selectedObject = this.getObjectListObjectFromDomElement(element);
      if (!selectedObject) { return; }
      const parentComputedHeight = window.getComputedStyle(element).getPropertyValue('height');
      const childComputedHeight  = window.getComputedStyle(element.firstElementChild).getPropertyValue('height');
      if (parseInt(parentComputedHeight, 10) < parseInt(childComputedHeight, 10)) {
        selectedObject.Height = parseInt(childComputedHeight, 10) + 2;
        setTimeout(() => {
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.UPDATED);
        }, 0);
      }
    },

    // Rotating Event
    onMoveableRotateStart(e) {
      this.setSnapThreshold(0);
      e.setFixedDirection([0, 0]);
    },

    onMoveableRotate(e) {
      e.target.style.transform = e.drag.transform;
    },

    onMoveableRotateEnd(ev) {
      const selectedObject = this.findObj(ev.target.id);
      if (!selectedObject || !ev.lastEvent) { return; }
      if (selectedObject.type === 'table') {
        EventBus.$emit('tableShowDots', selectedObject.Id);
      }
      selectedObject.Y           += ev.lastEvent.drag.beforeTranslate[1];
      selectedObject.X           += ev.lastEvent.drag.beforeTranslate[0];
      selectedObject.RotateDegree = ev.lastEvent.rotate;
      this.restoreMoveableElementFromModel(ev.target, selectedObject);
      this.setSnapThreshold(5);
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.refreshMoveableTargets();
      this.emitUndoRedoRequest('Called by rotate end');
    },

    onMoveableRotateGroupStart(e) {
      this.setSnapThreshold(0);
      e.setFixedDirection([0, 0]);
    },

    onMoveableRotateGroup(e) {
      e.events.forEach((ev) => {
        ev.target.style.transform = ev.drag.transform;
      });
    },

    onMoveableRotateGroupEnd(e) {
      this.setSnapThreshold(5);
      e.events.forEach((ev) => {
        if (!ev.lastEvent) { return; }
        const selectedObject = this.findObj(ev.target.id);
        if (!selectedObject) { return; }
        selectedObject.Y           += ev.lastEvent.drag.beforeTranslate[1];
        selectedObject.X           += ev.lastEvent.drag.beforeTranslate[0];
        selectedObject.RotateDegree = ev.lastEvent.beforeRotate;
        this.restoreMoveableElementFromModel(ev.target, selectedObject);
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      this.refreshMoveableTargets();
      this.emitUndoRedoRequest('Called by group rotate end');
    },

    // Selecto
    initSelectManager() {
      this.selectO = new Selecto({
        container:            this.container.parentNode,
        selectableTargets:    ['.html-container .target'],
        selectFromInside:     false,
        toggleContinueSelect: 'shift',
      })
          .on('dragStart', this.onSelectoDragStart)
          .on('select', this.onSelectoSelect)
          .on('selectEnd', this.onSelectoSelectEnd);
    },

    onDocumentDoubleClick(e) {
      if (!e || !this.container) { return; }
      const groupChild = this.findGroupChildAtEvent(e);
      if (!groupChild) { return; }
      e.preventDefault();
      e.stopPropagation();

      const isActiveChild = this.activeObject && this.activeObject.Id === groupChild.Id;
      if (isActiveChild && groupChild.type === 'text' && !groupChild.locked) {
        this.setupActiveEditor();
        return;
      }

      this.selectGroupChildById(groupChild.Id);
    },

    onSelectoDragStart(e) {
      EventBus.$emit(ApplicationEventTypes.AnimationEvent.STOP_OBJECT_ANIMATION);
      this.$store.commit('activeProduct/updateIsTrimmedVideoPlaying', false);
      this.$store.commit('activeProduct/updateISTrimmingEnable', false);
      this.$store.commit('activeProduct/updatePageAnimationPanel', false);
      const { moveableObj } = this;
      const { target } = e.inputEvent;
      if (moveableObj.isMoveableElement(target) || target.classList.contains('extended-button')) {
        e.stop();
      }
    },

    onSelectoSelect() {
      // While binding group/multi targets, Selecto fires 'select' and would
      // wipe activeObject — that collapses group selection to a single child.
      if (this.suppressSelectoSelectEnd) { return; }
      this.selectoPriorActive = this.activeObject;
      this.activeObject       = null;
      this.isObjectSelected   = true;
    },

    onSelectoSelectEnd(e) {
      if (this.suppressSelectoSelectEnd) {
        return;
      }
      const { moveableObj } = this;
      const { selected, inputEvent, isDragStart } = e;
      const priorActive = this.selectoPriorActive || this.activeObject;
      this.selectoPriorActive = null;

      if (selected.length > 0) {
        if (!selected[0].classList.contains('focus-text-editor')) {
          this.makeIsSelectedKeyFalse();

          const topLevelById = new Map();
          e.selected.forEach((selectedElem) => {
            let el = selectedElem;
            let object = el && el.id ? this.findObj(el.id) : null;
            while (!object && el && el !== this.container) {
              el = el.parentElement;
              if (el && el.id) { object = this.findObj(el.id); }
            }
            if (!object) { return; }
            const topLevel = this.resolveTopLevelSelection(object, priorActive);
            if (topLevel && this.canTransformObject(topLevel)) {
              topLevelById.set(topLevel.Id, topLevel);
            }
          });

          const selectedObjects = Array.from(topLevelById.values());
          selectedObjects.forEach((object) => { object.isSelected = true; });
          this.activeObject = selectedObjects[0] || null;

          this.updateMoveableControls(e);

          if (isDragStart && this.activeObject) {
            e.inputEvent.preventDefault();
            moveableObj.dragStart(inputEvent);
          }
          if (this.activeObject) {
            EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, this.activeObject);
            EventBus.$emit('unSelectAudioLayer');
          }
        }
        setTimeout(() => {
          this.moveableObj.dragTarget = '.drag-button';
        }, 400);
      } else {
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.CLEARED);
      }

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
      if (!isDragStart) {
        this.onObjectUpdated();
      }
      if (e.removed.length > 0 && e.removed[0].dataset.type === 'text') {
        this.disableTinymce();
      }
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED_OBJETCS, [this.selectO.selectedTargets]);
    },

    updateMoveableControls(e) {
      if (!this.activeObject) {
        this.moveableObj.target = null;
        return;
      }

      if (this.isTextEditModeActive && this.isTextEditModeActive()) {
        this.hideMoveableDuringTextEdit();
        return;
      }

      const selectedTopLevel = this.getSelectedTopLevelObjects();
      const selectedMoveable = this.getSelectedMoveableObjects();
      const isMultiSelection = this.isGroupSelected(selectedTopLevel)
          || selectedMoveable.length > 1
          || (e.selected && e.selected.length > 1);

      if (isMultiSelection && selectedMoveable.length > 0) {
        this.moveableObj.resizable = false;
        this.moveableObj.scalable  = true;
        this.moveableObj.keepRatio = true;
        this.moveableObj.props.cropHandle = false;
        this.moveableObj.props.textAIHandle = false;
        this.moveableObj.props.menuHandle = false;
        this.moveableObj.verticalGuidelines   = this.verticalGuides.concat([0, this.width]).map(num => Math.ceil(num * this.$store.getters.getZoomLevel / 100));
        this.moveableObj.horizontalGuidelines = this.horizontalGuides.concat([0, this.height]).map(num => Math.ceil(num * this.$store.getters.getZoomLevel / 100)).concat([0, this.width]);
        this.moveableObj.snapElement = this.isAlignmentGuideOn;
        this.moveableObj.snappable    = this.isAlignmentGuideOn;
        this.moveableObj.renderDirections = ['nw', 'ne', 'se', 'sw'];
        this.selectO.toggleContinueSelect = ['shift'];
        this.applyMultiSelectMoveableTargets(selectedMoveable);
        return;
      }

      if (!this.canTransformObject(this.activeObject)) {
        this.syncMoveableForLockState();
        return;
      }

      this.moveableObj.resizable = true;
      this.moveableObj.scalable  = false;
      this.moveableObj.keepRatio = false;
      this.moveableObj.props.cropHandle = !!(this.activeObject
          && this.activeObject.type === 'image'
          && this.activeObject.ImageType === 'Image'
          && this.activeObject.DefaultImageUrl
          && String(this.activeObject.DefaultImageUrl).split(/[#?]/)[0].split('.').pop().trim() !== 'svg'
          && e.selected.length === 1);
      this.moveableObj.props.textAIHandle = !!(this.activeObject && this.activeObject.type === 'text' && e.selected.length === 1);
      this.moveableObj.props.menuHandle = !!(this.activeObject && this.activeObject.type === 'table' && e.selected.length === 1);
      this.moveableObj.verticalGuidelines   = this.verticalGuides.concat([0, this.width]).map(num => Math.ceil(num * this.$store.getters.getZoomLevel / 100));
      this.moveableObj.horizontalGuidelines = this.horizontalGuides.concat([0, this.height]).map(num => Math.ceil(num * this.$store.getters.getZoomLevel / 100)).concat([0, this.width]);
      this.moveableObj.snapElement = this.isAlignmentGuideOn;
      this.moveableObj.snappable    = this.isAlignmentGuideOn;
      this.moveableObj.renderDirections = ['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se'];
      this.selectO.toggleContinueSelect = ['shift'];

      const selectedObjects  = e.selected.map(el => this.findObj(el && el.id)).filter(Boolean);
      const onlyEHFDICSelected = selectedObjects.length > 0 && selectedObjects.every(obj => obj.type === 'image' && (['FDICLogoWhite', 'FDICLogo', 'FDICText', 'FDICTextWhite'].includes(obj.ImageType)));

      if (e.selected.length > 1) {
        const unlockedTargets = e.selected.filter((el) => {
          const obj = el && el.id ? this.findObj(el.id) : null;
          return obj && this.canTransformObject(obj);
        });
        if (!unlockedTargets.length) {
          this.syncMoveableForLockState();
          return;
        }
        this.moveableObj.target           = unlockedTargets;
        this.moveableObj.resizable        = false;
        this.moveableObj.keepRatio        = true;
        this.moveableObj.scalable         = true;
        this.moveableObj.renderDirections = ['nw', 'ne', 'se', 'sw'];
        this.moveableObj.updateTarget();
        return;
      }

      if (e.selected.length === 1 && e.selected[0]) {
        const selectedObj = this.findObj(e.selected[0].id);
        if (!selectedObj || !this.canTransformObject(selectedObj)) {
          this.syncMoveableForLockState();
          return;
        }
        this.moveableObj.target = e.selected[0];
        this.moveableObj.updateTarget();
        if (this.activeObject.type === 'video' || onlyEHFDICSelected) {
          this.moveableObj.renderDirections = ['nw', 'ne', 'sw', 'se'];
          this.moveableObj.resizable        = { edge: [], renderDirections: ['nw', 'ne', 'sw', 'se'] };
        }
        if (this.activeObject.type === 'text') {
          this.moveableObj.resizable = { edge: ['e', 'w', 'n', 's'], renderDirections: ['e', 'w', 'n', 's'] };
          this.moveableObj.scalable  = { edge: [], renderDirections: ['nw', 'ne', 'se', 'sw'] };
        }
      }
    },

    makeIsSelectedKeyFalse() {
      clearAllSelection(this.objectList);
    },

    getObjectListObjectFromDomElement(element) {
      if (element && element.id) {
        const byId = this.findObj(element.id);
        if (byId) { return byId; }
      }
      try {
        return this.objectList[element.dataset.elementindex];
      } catch (ex) {
        return null;
      }
    },

    onClearSelection() {
      this.$store.commit('activeProduct/updateIsTrimmedVideoPlaying', false);
      this.$store.commit('activeProduct/updateISTrimmingEnable', false);
      this.$store.commit('activeProduct/updatePageAnimationPanel', false);
      this.$store.commit('activeProduct/updateDisplayAnimationPanel', false);
      this.$store.commit('activeProduct/UpdateIsAudioTrimOn', false);
      EventBus.$emit('unSelectAudioLayer');
      EventBus.$emit('toggleAudioLeftSectionAccordion', false);
      EventBus.$emit('onObjectSelectedCleared');
      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.MULTI_OBJETCS_CLEAR);
      this.makeIsSelectedKeyFalse();
      this.activeObject = null;
      this.targets      = [];
      this.selectO.setSelectedTargets([]);
      this.selectO.trigger('select');
      this.moveableObj.target = null;
      this.disableTinymce();
    },

    disableTinymce() {
      const element = document.querySelector('.focus-text-editor, .mce-edit-focus, .fmce-edit-focus');
      const self    = this;
      if (!element) { return; }

      this.setParentHeightAfterTextResize(element);
      element.classList.remove('focus-text-editor');
      window.getSelection().removeAllRanges();
      const tempObject = this.findObj(element.id);
      if (!tempObject) {
        this.activeEditor = null;
        return;
      }
      tempObject.isEditorEnable = false;
      if (this.activeEditor && this.activeEditor.targetElm) {
        tempObject.modifiedHtml = this.activeEditor.getContent();
      }
      tempObject.Value = tempObject.modifiedHtml;
      const activeElement = document.getElementById(tempObject.Id);
      if (activeElement) {
        activeElement.style.overflowY = 'initial';
        activeElement.classList.remove('focus-text-editor');
      }
      this.activeEditor = null;
      setTimeout(() => {
        this.removeFocusFromTextEditor(element);
        if (typeof this.activeObject !== 'undefined') {
          EventBus.$emit('onObjectSelected', this.activeObject);
        }
        setTimeout(() => { self.curveNewTextValues(); }, 10);
      }, 0);
    },

    curveNewTextValues() {
      const processTextObject = (object) => {
        if (object.type === 'text' && object.shapeType === 'curve' && object.Value !== object.curvedHtml) {
          object.unCurvedHtml = object.Value.indexOf('transform-origin') === -1 ? object.Value : object.unCurvedHtml;
          const elem = document.querySelector(`.canvas-non-play-all [id='${object.Id}'] > div`);
          if (!elem) { return; }
          this.traverseAndWrap(elem);
          this.curveText(elem, parseInt(object.curve, 10));
          setTimeout(() => {
            const newDimensions = this.adjustTargetElement(`.canvas-non-play-all [id='${object.Id}']`, object);
            object.Value        = elem.innerHTML;
            object.modifiedHtml = elem.innerHTML;
            object.curvedHtml   = elem.innerHTML;
            this.applyCurvedBoundsToTextObject(this.objectList, object, newDimensions);
            this.refreshMoveableTargets();
          });
        }
        if (object.type === 'group' && object.children) {
          object.children.forEach(processTextObject);
        }
      };
      this.objectList.forEach(processTextObject);
    },

    emitUndoRedoRequest(data) {
      EventBus.$emit(ApplicationEventTypes.UndoRedoEvents.ADDED, data);
    },

    removeFocusFromTextEditor(element) {
      element.blur();
    },

    onObjectUpdated: throttle(function () {
      this.$nextTick(() => {
        if (!this.activeObject || !this.moveableObj) {
          return;
        }
        if (this.isTextEditModeActive && this.isTextEditModeActive()) {
          this.hideMoveableDuringTextEdit();
          return;
        }
        if (!this.hasTransformableSelection()) {
          this.syncMoveableForLockState();
          return;
        }
        this.syncMoveableForLockState();

        const nsHandle = (this.activeObject.Width * this.activeObject.scaleX * (this.scale / 100) < 90) ? 0 : 1;
        const ewHandle = (this.activeObject.Height * this.activeObject.scaleY * (this.scale / 100) < 75) ? 0 : 1;
        this.$store.commit('setMovableHandle', { ns: nsHandle, ew: ewHandle });
        this.refreshMoveableTargets();
      });
    }),

    selectingCanvasObjectFromRightPanel(id) {
      const object = this.findObj(id);
      if (!object) { return; }
      if (findParentGroup(this.objectList, id)) {
        this.selectGroupChildById(id);
        return;
      }
      if (object.type === 'group') {
        this.selectMultipleObjects([object]);
        return;
      }
      const clickEvent = new MouseEvent('click', { view: window, bubbles: true, cancelable: true });
      this.selectO.clickTarget(clickEvent, document.getElementById(id));
    },

    selectGroupChildById(id) {
      const object = this.findObj(id);
      if (!object) { return; }
      const parentGroup = findParentGroup(this.objectList, id);
      const el = document.getElementById(id);

      this.makeIsSelectedKeyFalse();
      if (parentGroup) { parentGroup.isSelected = false; }
      object.isSelected = true;
      this.activeObject = object;

      if (el && this.moveableObj) {
        this.suppressSelectoSelectEnd = true;
        this.selectO.setSelectedTargets([el]);
        this.suppressSelectoSelectEnd = false;
        this.activeObject = object;
        object.isSelected = true;
        if (parentGroup) { parentGroup.isSelected = false; }
        this.updateMoveableControls({ selected: [el] });
        if (this.canTransformObject(object)) {
          this.moveableObj.updateTarget();
        }
      }

      EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED, this.activeObject);
      if (el) {
        EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECTED_OBJETCS, [[el]]);
      }
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.objectList);
    },

    findGroupChildAtEvent(e) {
      const elements = document.elementsFromPoint(e.clientX, e.clientY);
      for (let i = 0; i < elements.length; i += 1) {
        let el = elements[i];
        while (el && el !== this.container) {
          if (el.id && findParentGroup(this.objectList, el.id)) {
            return this.findObj(el.id);
          }
          el = el.parentElement;
        }
      }
      return null;
    },
  },
};
